import React, { useCallback, useEffect, useState } from 'react';
import { refreshCurrentUser, notifySessionRestored } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { t } from '@alterrego/core-i18n';
import { signInWithApple, isAppleSignInCancel, toAppleSignInError } from '@alterrego/module-apple-auth';
import { useFeatureFlag } from '@alterrego/module-branding';
import { GIFT_CARDS_ENABLED } from '../config';
import { apiFetch, apiUrl, TENANT_ID, ENV } from '../utils/api';
import { DebugOverlay } from '../components/DebugOverlay';
import './CompteScreen.css';

const APPLE_SERVICE_ID = 'com.alterrego.signin';
const APPLE_REDIRECT_URI = apiUrl('/auth/apple/callback');
const BUNDLE_ID = 'fr.saufimprevu.app';

const LOYALTY_LEVELS = [
  { level: 1, name: 'Eclaireur', emoji: '🔭', pointsRequired: 0, reward: 'Acces au programme' },
  { level: 2, name: 'Amateur', emoji: '🍷', pointsRequired: 100, reward: '1 verre offert' },
  { level: 3, name: 'Connaisseur', emoji: '📖', pointsRequired: 300, reward: '-10% sur la carte' },
  { level: 4, name: 'Epicurien', emoji: '🎩', pointsRequired: 600, reward: '1 planche offerte' },
  { level: 5, name: 'Passionne', emoji: '💎', pointsRequired: 1000, reward: 'Acces degustations privees' },
  { level: 6, name: 'Expert', emoji: '🏅', pointsRequired: 1500, reward: '-15% permanent' },
  { level: 7, name: 'Maitre de chai', emoji: '🗝️', pointsRequired: 2500, reward: 'Bouteille prestige offerte' },
  { level: 8, name: 'Ambassadeur', emoji: '👑', pointsRequired: 4000, reward: 'Soiree privee + -20%' },
];

export function CompteScreen() {
  const navigate = useNavigate();
  const hasCave = useFeatureFlag('cave');
  const [user, setUser] = useState<{ email: string; name: string; isAdmin?: boolean } | null>(() => {
    try {
      const saved = localStorage.getItem('alterrego_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  // Auto-refresh isAdmin depuis /users/me au mount si user déjà connecté.
  // Migre les sessions legacy (avant ajout du champ isAdmin) sans re-login.
  useEffect(() => {
    if (!user) return;
    refreshCurrentUser().then((updated) => {
      if (updated) setUser(updated);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [signingIn, setSigningIn] = useState(false);
  const [debugLines, setDebugLines] = useState<string[]>([]);

  const addDebug = useCallback((msg: string) => {
    const ts = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const line = `[${ts}] ${msg}`;
    console.log('[APPLE AUTH DEBUG]', line);
    setDebugLines(prev => [...prev.slice(-8), line]);
  }, []);

  // Retour du flux web : le backend nous renvoie ici avec les jetons dans le
  // fragment (cf. /auth/apple/callback). C'est le chemin NOMINAL sur le web —
  // le mode popup du SDK Apple ne marche pas hors de l'origine du backend.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.includes('jwt=')) return;
    const params = new URLSearchParams(hash.substring(1));
    const jwt = params.get('jwt');
    const userParam = params.get('user');
    if (jwt && userParam) {
      try {
        const u = JSON.parse(decodeURIComponent(userParam));
        const name = u.displayName || u.email || 'Utilisateur';
        // isAdmin doit venir du backend, comme dans le flux exchange : le
        // déduire de l'email casse avec « Hide My Email » d'Apple.
        const userObj = { email: u.email ?? '', name, isAdmin: u.isAdmin === true };
        setUser(userObj);
        localStorage.setItem('alterrego_user', JSON.stringify(userObj));
        localStorage.setItem('alterrego_jwt', jwt);
        const refreshToken = params.get('refreshToken');
        if (refreshToken) localStorage.setItem('alterrego_refresh_token', refreshToken);
        notifySessionRestored();
        addDebug('redirect callback OK — jwt persisté');
      } catch {}
      // Purge le fragment : les jetons ne doivent pas rester dans la barre
      // d'URL ni dans l'historique.
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [addDebug]);

  const handleAppleSignIn = useCallback(async () => {
    setSigningIn(true);
    setDebugLines([]);
    try {
      addDebug('étape 1/4 — signInWithApple() →');
      const result = await signInWithApple({
        serviceId: APPLE_SERVICE_ID,
        redirectUri: APPLE_REDIRECT_URI,
        // Web : la page part sur Apple et `signInWithApple` ne rend jamais la
        // main — le retour se fait par le fragment, géré dans l'effet ci-dessus.
        // `tenantId`/`env` doivent rester ceux qu'on émet partout ailleurs,
        // sinon le JWT est frappé dans un espace que les appels suivants
        // n'utilisent pas.
        state: {
          returnUrl: `${window.location.origin}${window.location.pathname}`,
          tenantId: TENANT_ID,
          env: ENV,
        },
      });
      addDebug(`étape 2/4 — code=${result.authorizationCode.slice(0, 12)}… idToken=${result.identityToken.slice(0, 12)}…`);

      addDebug('étape 3/4 — POST /auth/apple/exchange →');
      const res = await apiFetch('/auth/apple/exchange', {
        method: 'POST',
        auth: false,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: result.authorizationCode,
          idToken: result.identityToken,
          bundleId: BUNDLE_ID,
        }),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => '(empty)');
        addDebug(`exchange FAILED: ${res.status} — ${body.slice(0, 120)}`);
        throw new Error(`Exchange failed ${res.status}: ${body.slice(0, 200)}`);
      }

      const data = await res.json();
      addDebug(`étape 4/4 — exchange OK, jwt=${String(data.token ?? data.jwt ?? '').slice(0, 12)}…`);

      const jwt = data.token ?? data.jwt;
      const refreshToken = data.refreshToken ?? null;
      const email = result.email ?? data.user?.email ?? '';
      const givenName = result.fullName?.givenName ?? data.user?.displayName ?? '';
      const familyName = result.fullName?.familyName ?? '';
      const name = [givenName, familyName].filter(Boolean).join(' ') || email || 'Utilisateur';
      // isAdmin doit venir du backend (source de vérité) — pas dérivable côté
      // client à partir de l'email seul, surtout si l'user a utilisé "Hide My
      // Email" (email = xxxx@privaterelay.appleid.com).
      const userObj = { email, name, isAdmin: data.user?.isAdmin === true };

      setUser(userObj);
      localStorage.setItem('alterrego_user', JSON.stringify(userObj));
      localStorage.setItem('alterrego_jwt', jwt);
      if (refreshToken) localStorage.setItem('alterrego_refresh_token', refreshToken);
      // Reconnexion réussie → réarme la bannière globale de session expirée.
      notifySessionRestored();
    } catch (err: unknown) {
      // `signInWithApple` normalise tout rejet en AppleSignInError : on lit le
      // code Apple (invalid_client…) plutôt que de stringifier un objet nu, qui
      // donnait « Connexion Apple échouée : [object Object] ».
      const error = toAppleSignInError(err);
      addDebug(`ERREUR: [${error.code}] ${error.message}`);
      if (!isAppleSignInCancel(error)) {
        alert(`Connexion Apple échouée : ${error.message}`);
      }
    } finally {
      setSigningIn(false);
    }
  }, [addDebug]);

  const signOut = useCallback(() => {
    setUser(null);
    localStorage.removeItem('alterrego_jwt');
    localStorage.removeItem('alterrego_user');
  }, []);

  const currentLevelIndex = 3;
  const points = 320;
  const currentLevel = LOYALTY_LEVELS[currentLevelIndex];
  const nextLevel = LOYALTY_LEVELS[currentLevelIndex + 1];
  const progress = nextLevel ? (points / nextLevel.pointsRequired) * 100 : 100;

  return (
    <div className="compte">
      <div className="compte-header">
        <h1>Mon espace perso</h1>
        {user ? (
          <button className="logout-btn" onClick={signOut}>{t('compte_deconnexion')}</button>
        ) : (
          <button className="login-btn" onClick={handleAppleSignIn} disabled={signingIn}>
            {signingIn ? '...' : t('compte_connecter')}
          </button>
        )}
      </div>

      {user && (
        <div className="compte-section">
          <div className="profile-card">
            <div className="profile-avatar">{user.name.charAt(0).toUpperCase()}</div>
            <div className="profile-info">
              <strong>{user.name}</strong>
              <span>{user.email}</span>
            </div>
          </div>
        </div>
      )}

      {/* Réglages staff (admin) — items masqués aux clients ; les écrans cibles
          re-gardent l'accès admin (défense en profondeur). */}
      {user?.isAdmin && (
        <div className="compte-section">
          <h2>Réglages</h2>
          <button className="service-card" onClick={() => navigate('/compte/notifications')}>
            <div className="service-icon" style={{ background: '#1464B412' }}>🔔</div>
            <div className="service-text">
              <strong>Notifications</strong>
              <span>Alertes sécurité, clôtures Z, Pennylane</span>
            </div>
            <span className="chevron">›</span>
          </button>
        </div>
      )}

      {/* Services */}
      <div className="compte-section">
        <h2>Services</h2>
        {[
          // Livre de cave : même flag `features.cave` que l'accès rapide du Home.
          // Un bar à cocktails (Nodo) n'a pas de cave à exposer.
          ...(hasCave ? [{ icon: '🍷', label: t('cave_titre'), desc: 'Vos coups de coeur', color: '#1464B4', to: '/cave' }] : []),
          ...(GIFT_CARDS_ENABLED ? [{ icon: '🎁', label: t('compte_carte_cadeau'), desc: 'Offrir une carte', color: '#c8a96e', to: '/carte-cadeau' }] : []),
          { icon: '🔗', label: t('compte_parrainage'), desc: '50 pts par filleul', color: '#6C5CE7' },
        ].map((item, i) => (
          <button key={i} className="service-card" onClick={() => item.to && navigate(item.to)}>
            <div className="service-icon" style={{ background: item.color + '12' }}>{item.icon}</div>
            <div className="service-text">
              <strong>{item.label}</strong>
              <span>{item.desc}</span>
            </div>
            <span className="chevron">›</span>
          </button>
        ))}
      </div>

      {/* Loyalty levels */}
      <div className="compte-section">
        <h2>Les {LOYALTY_LEVELS.length} niveaux</h2>
        <div className="levels-card">
          {LOYALTY_LEVELS.map((l, i) => (
            <div key={i} className={`level-row ${i <= currentLevelIndex ? 'reached' : ''} ${i === currentLevelIndex ? 'current' : ''}`}>
              <div className={`level-badge ${i <= currentLevelIndex ? 'active' : ''}`}>{l.level}</div>
              <span className="level-emoji">{l.emoji}</span>
              <div className="level-info">
                <span className="level-name">{l.name}</span>
                <span className="level-reward">{l.reward}</span>
              </div>
              <span className="level-pts">{l.pointsRequired} pts</span>
            </div>
          ))}
        </div>
      </div>
      <DebugOverlay lines={debugLines} label="Apple Sign-In" color="#88ccff" />
    </div>
  );
}
